import { cn } from '@lyearn/style';
import { Checkbox, Stack, Switch, Typography } from '@lyearn/ui';
import { ResourceTypeEnum, ResourceVersionTypeEnum } from '@/types/schema';

import usePublishChanges from '../usePublishChanges';

type UsePublishChangesReturnType = ReturnType<typeof usePublishChanges>;

export type PublishOptionsProps = {
  publishOptions: UsePublishChangesReturnType['publishOptions'];
  onRecalibrateChange: UsePublishChangesReturnType['onRecalibrateChange'];
  onVersionChange: UsePublishChangesReturnType['onVersionChange'];
  publishConfig: UsePublishChangesReturnType['publishConfig'];
  allowMinorVersionPublish?: boolean;
  contentType?: ResourceTypeEnum;
};

const isCourseOrPath = (contentType: ResourceTypeEnum | undefined): boolean => {
  return (
    Boolean(contentType) &&
    (contentType === ResourceTypeEnum.Course || contentType === ResourceTypeEnum.Path)
  );
};

export default function PublishOptions(props: PublishOptionsProps) {
  const {
    publishOptions,
    onRecalibrateChange,
    onVersionChange,
    publishConfig,
    allowMinorVersionPublish,
    contentType,
  } = props;

  return (
    <Stack direction="col" gapY={12}>
      {publishOptions.map((option) => {
        const isSelected = option.id === publishConfig.versionType;
        const { disabled } = option;
        const isSwitchDisabled =
          option.id === ResourceVersionTypeEnum.Major && !allowMinorVersionPublish;
        return (
          <Stack
            key={option.id}
            className={cn(
              'rounded-12 border border-solid',
              isSelected ? 'border-interactive-primary' : 'border-bg-onclick',
            )}
            data-id={option.id}
            direction="col">
            <Stack
              alignItems="center"
              className={cn(
                'w-full p-20',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              )}
              data-disabled={disabled}
              gapX={24}
              justifyContent="between"
              onClick={disabled ? undefined : () => onVersionChange(option.id)}>
              <Stack direction="col" gapY={4}>
                <Typography
                  color={disabled ? 'text-text-secondary' : 'text-text-primary'}
                  variant="body-short-01">
                  {option.text}
                </Typography>
                <Typography className="mt-4" color="text-text-secondary" variant="body-short-02">
                  {option.description}
                </Typography>
              </Stack>
              <Checkbox
                checked={isSelected}
                className="h-24 w-24"
                disabled={disabled}
                size="large"
                variant="circle"
              />
            </Stack>
            {isSelected && option.options?.length ? (
              <Stack
                className="border-t border-interactive-secondary-hover px-12 pt-8 pb-12"
                direction="col">
                {option.options.map((opt) => {
                  const isOptionSelected = Boolean(publishConfig.recalibrateConfig?.[opt.id]);
                  return (
                    <Stack
                      key={opt.id}
                      alignItems="center"
                      className="h-[4.6rem] px-8"
                      gapY={16}
                      justifyContent="between"
                      onClick={() => onRecalibrateChange(opt.id, !isOptionSelected)}>
                      <Typography color="text-text-secondary" variant="body-short-02">
                        {opt.text}
                      </Typography>
                      {isCourseOrPath(contentType) ? (
                        <Switch
                          checked={isOptionSelected}
                          className={cn(
                            isSwitchDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                          )}
                          disabled={false}
                          size="small"
                        />
                      ) : (
                        <Switch
                          checked={isSwitchDisabled ? true : isOptionSelected}
                          className={cn(
                            isSwitchDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                          )}
                          disabled={isSwitchDisabled}
                          size="small"
                        />
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
}
